import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { clearRememberMe } from "@/lib/remember-me";
import { Lock, ArrowRight, HeartHandshake, Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Set a new password — Recovery Trauma™" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);
  // Distinguishes bad/expired recovery links from ordinary form validation.
  const [linkStatus, setLinkStatus] = useState<
    | { kind: "ok" }
    | { kind: "expired"; detail?: string }
    | { kind: "invalid"; detail?: string }
    | { kind: "missing" }
  >({ kind: "ok" });

  useEffect(() => {
    let cancelled = false;
    const finishReady = () => {
      if (!cancelled) setReady(true);
    };
    const prepareResetSession = async () => {
      const params = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const urlErrorCode =
        params.get("error_code") || hash.get("error_code") || params.get("error") || hash.get("error");
      const urlError = params.get("error_description") || hash.get("error_description");
      const classify = (detail?: string): "expired" | "invalid" => {
        const s = `${urlErrorCode ?? ""} ${detail ?? ""}`.toLowerCase();
        if (s.includes("expired") || s.includes("otp_expired")) return "expired";
        return "invalid";
      };

      if (urlError || urlErrorCode) {
        if (!cancelled) setLinkStatus({ kind: classify(urlError ?? undefined), detail: urlError ?? undefined });
        finishReady();
        return;
      }

      const code = params.get("code");
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError && !cancelled) {
          setLinkStatus({ kind: classify(exchangeError.message), detail: exchangeError.message });
        }
        window.history.replaceState(null, "", "/reset-password");
        finishReady();
        return;
      }

      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");
      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (sessionError && !cancelled) {
          setLinkStatus({ kind: classify(sessionError.message), detail: sessionError.message });
        }
        window.history.replaceState(null, "", "/reset-password");
        finishReady();
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (!data.session && !cancelled) {
        setLinkStatus({ kind: "missing" });
      }
      finishReady();
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION") finishReady();
    });
    void prepareResetSession();
    return () => sub.subscription.unsubscribe();
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (password.length < 8) {
      setError("Please use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setError("Your reset link has expired or was already used. Please request a new password reset email.");
        setLoading(false);
        return;
      }
      const updatePromise = supabase.auth.updateUser({ password });
      const timeoutPromise = new Promise<{ error: { message: string } }>((resolve) =>
        setTimeout(
          () => resolve({ error: { message: "Request timed out. Please check your connection and try again." } }),
          15000,
        ),
      );
      const { error } = (await Promise.race([updatePromise, timeoutPromise])) as { error: { message: string } | null };
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      setSuccess(true);
      setLoading(false);
      // Invalidate every existing session (this device and all others) so the
      // user must sign in again with the new password.
      try {
        await supabase.auth.signOut({ scope: "global" });
      } catch {
        /* fall through — local sign-out below still clears this device */
      }
      clearRememberMe();
      setTimeout(() => navigate({ to: "/login", replace: true }), 2500);
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)]">
            <HeartHandshake className="h-6 w-6 text-foreground" />
          </div>
          <h1 className="mt-4 font-display text-2xl text-foreground">
            Set a new password
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose something kind, and easy to remember.
          </p>
        </div>

        {!ready ? (
          <p className="rounded-lg bg-cream/60 p-3 text-center text-xs text-muted-foreground">
            Preparing your reset session...
          </p>
        ) : linkStatus.kind !== "ok" ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <p className="font-display text-base text-foreground">
              {linkStatus.kind === "expired"
                ? "This reset link has expired"
                : linkStatus.kind === "missing"
                  ? "No reset link found"
                  : "This reset link isn't valid"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {linkStatus.kind === "expired"
                ? "Reset links stay valid for a short time for your safety. Please request a fresh one and we'll email it to you."
                : linkStatus.kind === "missing"
                  ? "It looks like you landed here without a reset link. Start by asking for a new password reset email."
                  : "This link may have already been used or was copied incorrectly. Please request a new password reset email."}
            </p>
            {linkStatus.kind !== "missing" && linkStatus.detail && (
              <p className="mt-3 rounded-md bg-background/60 p-2 text-[11px] text-muted-foreground">
                Details: {linkStatus.detail}
              </p>
            )}
            <div className="mt-5 flex flex-col gap-2">
              <Link
                to="/forgot-password"
                className="btn-gold inline-flex min-h-[44px] w-full items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Request a new reset link
              </Link>
              <Link
                to="/login"
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                New password
              </label>
              <div className="relative">
                <Lock aria-hidden="true" className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  id="reset-new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-input bg-background py-2 ps-9 pe-12 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  aria-controls="reset-new-password"
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                </button>
                <span aria-live="polite" className="sr-only">{showPassword ? "Password is visible" : "Password is hidden"}</span>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Confirm password
              </label>
              <div className="relative">
                <Lock aria-hidden="true" className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  id="reset-confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-input bg-background py-2 ps-9 pe-12 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  aria-controls="reset-confirm-password"
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                </button>
              </div>
            </div>


            {error && (
              <p className="rounded-lg bg-destructive/10 p-2.5 text-xs text-destructive">
                {error}
              </p>
            )}
            {success && (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                <p className="font-semibold">✓ New password saved</p>
                <p className="mt-1 text-xs">
                  For your safety, we've signed you out of every device. Redirecting you to sign in with your new password...
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || success}
              onClick={(ev) => {
                if (loading || success || !password || !confirm) return;
                ev.preventDefault();
                if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                handleSubmit(ev as unknown as React.FormEvent);
              }}
              className="btn-gold relative z-20 min-h-[48px] w-full touch-manipulation justify-center"
            >
              {loading ? "Saving..." : success ? "Saved" : "Save new password"}
              <ArrowRight className="h-4 w-4" />
            </button>

          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
